import React, { useContext, useState } from 'react';
import { useCanvas } from '../../../context/CanvasContext';
import { useEditorUI } from '../../../context/EditorUIContext';
import EditSidebarTabs from './EditSidebarTabs';
import textStyles from './styles/EditSidebarText.module.css';
import settingsStyles from './styles/EditSidebarSettings.module.css';
import keyStyles from './styles/EditSidebarKeys.module.css';
import LinkSituation from './LinkSituation';
import DeleteModal from './DeleteModal';
import useIsMobile from '../../../utils/useIsMobile';
import TextEditor from '../../texteditor/TextEditor';
import tabStyles from './styles/EditSidebarTabs.module.css';
import { FaExpand } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import AuthContext from '../../../context/AuthContext';
import ScenarioTextEditorModal from './ScenarioTextEditorModal';
import { TUTORIAL_TARGETS, tutorialAttr, useTutorialLock } from '../../../tutorials';
import KeyBadge, { KeyRoleIcon } from './keys/KeyBadge';
import KeyPicker from './keys/KeyPicker';
import KeyListEditor from './keys/KeyListEditor';
import useKeyHoverHighlight from './keys/useKeyHoverHighlight';
import { KEY_VISUALS } from './keys/keyVisuals';
import {
    KEY_LIST_FIELDS,
    VISIBILITY_MODES,
    keyName,
    patchForVisibilityMode,
    visibilityMode,
} from './keys/keyGraph';
import keysUi from './keys/styles/Keys.module.css';

const VISIBILITY_OPTIONS = [
    { mode: VISIBILITY_MODES.EVERYONE, label: 'Everyone' },
    { mode: VISIBILITY_MODES.HAS, label: 'Only players who HAVE a key' },
    { mode: VISIBILITY_MODES.LACKS, label: 'Only players who DO NOT have a key' },
];

/**
 * Edits one choice.
 *
 * The subject is the edge, looked up from the graph by id, so it is always the
 * current draft. Fields commit on blur as `updateEdgeData` ops.
 *
 * Note on hints: a hint is not a flag any more, it is a shape. A choice that
 * points back at its own situation IS a hint — `Choice.hasHint` is derived from
 * exactly that at publish time — so the hint text is offered whenever the edge is
 * a self-loop rather than behind a switch that could only ever be enabled in that
 * same case.
 *
 * @param {{edgeId: string}} props
 */
const EditChoice = ({ edgeId, setRenderEditSidebar }) => {
    const { getEdge, updateChoice, deleteChoice, keys } = useCanvas();
    const { activeEditTab, selectedChoiceQueue } = useEditorUI();
    const { userId: authUserId } = useContext(AuthContext);

    const isMobile = useIsMobile();
    const locked = useTutorialLock('editor');
    const hoverKey = useKeyHoverHighlight();

    const edge = getEdge(edgeId);
    const data = edge?.data || {};

    // Seeded once per choice: EditSidebar keys this panel by edge id, so an op
    // landing for this edge cannot clobber in-progress typing.
    const [title, setTitle] = useState(data.title || '');
    const [text, setText] = useState(data.text || '');
    const [hintText, setHintText] = useState(data.hint || '');

    // A gated mode the author has chosen but not yet given a key. Nothing is
    // committed until the key is picked, so no half-configured gate is written.
    const [pendingMode, setPendingMode] = useState(null);
    const [isChangingKey, setIsChangingKey] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isTextEditorModalOpen, setIsTextEditorModalOpen] = useState(false);

    const isHint = !!edge && edge.source === edge.target;

    // The committed mode, and what the radio shows while a key is being picked.
    const mode = visibilityMode(data);
    const shownMode = pendingMode ?? mode;
    const gateKeyId = mode === VISIBILITY_MODES.EVERYONE ? null : data.key;

    const queue = selectedChoiceQueue[edgeId];
    const isWaiting = !!queue && queue[0] !== authUserId;

    const commit = (patch) => updateChoice(edgeId, patch);

    /**
     * Moves the choice to a visibility mode. The key and both flags always go
     * out as one patch.
     *
     * A gated mode reuses the choice's current key when it has one — including
     * an old choice that has a key but neither flag, which the player treats as
     * visible to everyone — and otherwise opens the picker and waits.
     */
    const setVisibilityMode = (nextMode, keyId = data.key || null) => {
        if (nextMode === VISIBILITY_MODES.EVERYONE) {
            setPendingMode(null);
            setIsChangingKey(false);
            if (mode !== VISIBILITY_MODES.EVERYONE || data.key) {
                commit(patchForVisibilityMode(VISIBILITY_MODES.EVERYONE));
            }
            return;
        }
        if (!keyId) {
            setPendingMode(nextMode);
            return;
        }
        setPendingMode(null);
        setIsChangingKey(false);
        const patch = patchForVisibilityMode(nextMode, keyId);
        if (patch) commit(patch);
    };

    const confirmDeleteChoice = () => {
        deleteChoice(edgeId);
        setIsDeleteModalOpen(false);
        if (isMobile && typeof setRenderEditSidebar === 'function') setRenderEditSidebar(false);
    };

    if (!edge) return null;
    if (isWaiting) return <div>Waiting for a user to finish working on this edge</div>;

    return (
        <div className={tabStyles.typeChoice}>
            <EditSidebarTabs editType="choice" />

            {activeEditTab === '1' && (
                <div className={textStyles.textContainer}>
                    <div className={textStyles.titleRow}>
                        <input
                            type="text"
                            value={title}
                            onChange={(event) => setTitle(event.target.value)}
                            onBlur={() => {
                                if (title !== (data.title || '')) commit({ title });
                            }}
                            className={textStyles.titleInput}
                            readOnly={locked}
                            {...tutorialAttr(TUTORIAL_TARGETS.EDIT_CHOICE_TITLE)}
                        />
                        <button
                            className={textStyles.expandButton}
                            onClick={() => setIsTextEditorModalOpen(true)}
                            data-tooltip-id="preview_tooltip"
                            data-tooltip-content="Expand Editor"
                            disabled={locked}
                        >
                            <FaExpand />
                        </button>
                    </div>
                    <div className={textStyles.editorWrapper} {...tutorialAttr(TUTORIAL_TARGETS.EDIT_CHOICE_TEXT)}>
                        <TextEditor
                            initialData={text}
                            onChange={setText}
                            onBlur={(html) => {
                                if (html !== (data.text || '')) commit({ text: html });
                            }}
                            readOnly={locked}
                        />
                    </div>
                </div>
            )}

            {activeEditTab === '2' && <LinkSituation edgeId={edgeId} />}

            {activeEditTab === '3' && (
                <div className={settingsStyles.settingsContainer}>
                    <div className={settingsStyles.settingItem}>
                        <label>Set Correctness</label>
                        <div className={settingsStyles.radioGroup}>
                            <button
                                className={`${settingsStyles.radioButton} ${
                                    !data.correctness ? settingsStyles.activeNeutral : ''
                                }`}
                                onClick={() => commit({ correctness: null })}
                            >
                                Neutral
                            </button>
                            <button
                                className={`${settingsStyles.radioButton} ${
                                    data.correctness === 'correct' ? settingsStyles.activeCorrect : ''
                                }`}
                                onClick={() => commit({ correctness: 'correct' })}
                            >
                                Correct
                            </button>
                            <button
                                className={`${settingsStyles.radioButton} ${
                                    data.correctness === 'incorrect'
                                        ? settingsStyles.activeIncorrect
                                        : ''
                                }`}
                                onClick={() => commit({ correctness: 'incorrect' })}
                            >
                                Incorrect
                            </button>
                        </div>
                    </div>

                    <div className={settingsStyles.settingItem}>
                        <label>Hint</label>
                        <span>
                            {isHint
                                ? 'This choice loops back to its own situation, so it is a hint.'
                                : 'Link this choice back to its own situation to make it a hint.'}
                        </span>
                    </div>

                    {isHint && (
                        <div className={settingsStyles.expandedSettingItem}>
                            <TextEditor
                                initialData={hintText}
                                onChange={setHintText}
                                onBlur={(html) => {
                                    if (html !== (data.hint || '')) commit({ hint: html });
                                }}
                            />
                        </div>
                    )}

                    <button
                        onClick={() => setIsDeleteModalOpen(true)}
                        className={textStyles.deleteButton}
                    >
                        Delete Choice
                    </button>
                </div>
            )}

            {activeEditTab === '4' && (
                <div className={keyStyles.keysContainer}>
                    <KeyListEditor
                        key={`grants-${edgeId}`}
                        field={KEY_LIST_FIELDS.GRANTS}
                        subject="choice"
                        data={data}
                        commit={commit}
                        tutorialTarget={TUTORIAL_TARGETS.EDIT_KEYS_ADD}
                    />
                    <KeyListEditor
                        key={`removes-${edgeId}`}
                        field={KEY_LIST_FIELDS.REMOVES}
                        subject="choice"
                        data={data}
                        commit={commit}
                        tutorialTarget={TUTORIAL_TARGETS.EDIT_KEYS_REMOVE}
                    />

                    <div
                        className={keyStyles.visibilityContainer}
                        {...tutorialAttr(TUTORIAL_TARGETS.EDIT_KEYS_VISIBILITY)}
                    >
                        <h3>Who can see this choice?</h3>
                        <div className={keysUi.modeGroup} role="radiogroup">
                            {VISIBILITY_OPTIONS.map((option) => {
                                const active = shownMode === option.mode;
                                const visual = KEY_VISUALS[option.mode];
                                return (
                                    <label
                                        key={option.mode}
                                        className={`${keysUi.modeRow} ${active ? keysUi.active : ''}`}
                                        style={
                                            visual
                                                ? { '--role-color': visual.color, '--role-bg': visual.bg }
                                                : undefined
                                        }
                                    >
                                        <input
                                            type="radio"
                                            name={`visibility-${edgeId}`}
                                            checked={active}
                                            onChange={() => setVisibilityMode(option.mode)}
                                        />
                                        <span className={keysUi.modeText}>
                                            {visual && <KeyRoleIcon role={option.mode} size={12} />}
                                            {option.label}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>

                        {shownMode !== VISIBILITY_MODES.EVERYONE && (
                            <>
                                {gateKeyId && !pendingMode && !isChangingKey && (
                                    <div className={keysUi.gateSummary}>
                                        <span>
                                            {shownMode === VISIBILITY_MODES.HAS
                                                ? 'Shown only to players who have:'
                                                : 'Shown only to players who do not have:'}
                                        </span>
                                        <KeyBadge
                                            role={shownMode}
                                            name={keyName(keys, gateKeyId)}
                                            onHoverChange={hoverKey(gateKeyId)}
                                        />
                                        <button
                                            type="button"
                                            className={keysUi.linkButton}
                                            onClick={() => setIsChangingKey(true)}
                                        >
                                            Change key
                                        </button>
                                    </div>
                                )}

                                {(pendingMode || isChangingKey) && (
                                    <KeyPicker
                                        heading={isChangingKey ? 'Pick a different key' : 'Which key?'}
                                        excludeIds={isChangingKey && gateKeyId ? [gateKeyId] : []}
                                        onPick={(keyId) => {
                                            setIsChangingKey(false);
                                            setVisibilityMode(shownMode, keyId);
                                        }}
                                        onCancel={() => {
                                            setPendingMode(null);
                                            setIsChangingKey(false);
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Modals */}
            {isTextEditorModalOpen && (
                <ScenarioTextEditorModal
                    onClose={() => setIsTextEditorModalOpen(false)}
                    initialText={text}
                    onSave={(textToSave) => {
                        setText(textToSave);
                        commit({ text: textToSave });
                    }}
                    situationTitle={title}
                />
            )}

            {isDeleteModalOpen && (
                <DeleteModal
                    onConfirm={confirmDeleteChoice}
                    onCancel={() => setIsDeleteModalOpen(false)}
                    type={'Choice'}
                />
            )}

            <Tooltip
                id="preview_tooltip"
                place="left"
                type="dark"
                effect="solid"
                style={{ zIndex: 9999 }}
            />
        </div>
    );
};

export default EditChoice;
